import React, { useState, useEffect } from 'react';

function ProgramariNew() {
    // State for patient information (bare minimum fields)
    const [patientData, setPatientData] = useState({
        nume: '',
        prenume: '',
        email: '',
        telefon: '',
        sex: ''
    });

    // State for appointment details
    const [appointmentData, setAppointmentData] = useState({
        id_specialitate: '',
        id_medic: '',
        data: '',
        ora: '',
        observatii: ''
    });

    // State for dropdown data
    const [specialties, setSpecialties] = useState([]);
    const [doctors, setDoctors] = useState([]);

    // UI state
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [step, setStep] = useState(1); // 1 = patient info, 2 = appointment details

    // Load specialties on component mount
    useEffect(() => {
        loadSpecialties();
    }, []);

    // Load doctors when specialty changes
    useEffect(() => {
        if (appointmentData.id_specialitate) {
            loadDoctorsBySpecialty(appointmentData.id_specialitate);
        } else {
            setDoctors([]);
        }
    }, [appointmentData.id_specialitate]);

    const loadSpecialties = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${process.env.REACT_APP_API_URL}/v1/public/specialitati/list`, {
                headers: {
                    'Authorization': `Bearer ${process.env.REACT_APP_API_TOKEN}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            setSpecialties(data.items || []);
        } catch (err) {
            console.error('Error loading specialties:', err);
            setError('Eroare la încărcarea specialităților');
        } finally {
            setLoading(false);
        }
    };

    const loadDoctorsBySpecialty = async (specialtyId) => {
        try {
            setLoading(true);
            const response = await fetch(`${process.env.REACT_APP_API_URL}/v1/public/medici/list?id_specialitate=${specialtyId}`, {
                headers: {
                    'Authorization': `Bearer ${process.env.REACT_APP_API_TOKEN}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            setDoctors(data.items || []);
        } catch (err) {
            console.error('Error loading doctors:', err);
            setError('Eroare la încărcarea medicilor');
        } finally {
            setLoading(false);
        }
    };

    const handlePatientChange = (field, value) => {
        setPatientData(prev => ({
            ...prev,
            [field]: value
        }));
        setError('');
        setMessage('');
    };

    const handleAppointmentChange = (field, value) => {
        setAppointmentData(prev => ({
            ...prev,
            [field]: value
        }));
        setError('');
        setMessage('');
    };

    const validatePatientInfo = () => {
        if (!patientData.nume.trim()) {
            setError('Numele este obligatoriu');
            return false;
        }
        if (!patientData.prenume.trim()) {
            setError('Prenumele este obligatoriu');
            return false;
        }
        if (!patientData.email.trim()) {
            setError('Email-ul este obligatoriu');
            return false;
        }
        if (!patientData.telefon.trim()) {
            setError('Numărul de telefon este obligatoriu');
            return false;
        }
        if (!patientData.sex) {
            setError('Sexul este obligatoriu');
            return false;
        }
        return true;
    };

    const validateAppointmentDetails = () => {
        if (!appointmentData.id_specialitate) {
            setError('Specialitatea este obligatorie');
            return false;
        }
        if (!appointmentData.id_medic) {
            setError('Medicul este obligatoriu');
            return false;
        }
        if (!appointmentData.data) {
            setError('Data este obligatorie');
            return false;
        }
        if (!appointmentData.ora) {
            setError('Ora este obligatorie');
            return false;
        }
        return true;
    };

    const handleNextStep = () => {
        if (step === 1 && validatePatientInfo()) {
            setStep(2);
        }
    };

    const handlePrevStep = () => {
        if (step === 2) {
            setStep(1);
        }
    };

    const createAppointment = async () => {
        if (!validateAppointmentDetails()) {
            return;
        }

        try {
            setLoading(true);

            // Step 1: Create patient
            const patientResponse = await fetch(`${process.env.REACT_APP_API_URL}/v1/public/pacienti`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${process.env.REACT_APP_API_TOKEN}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    ...patientData,
                    nationalitate: 'romania'
                })
            });

            if (!patientResponse.ok) {
                throw new Error('Eroare la crearea pacientului');
            }

            const patientDataResult = await patientResponse.json();

            if (!patientDataResult.ok || !patientDataResult.item) {
                throw new Error(patientDataResult.error || 'Eroare la crearea pacientului');
            }

            const patientId = patientDataResult.item.id;

            // Step 2: Create appointment
            const appointmentPayload = {
                id_medic: parseInt(appointmentData.id_medic),
                id_pacient: parseInt(patientId),
                data: appointmentData.data,
                ora: appointmentData.ora,
                durata: 30, // Default duration
                observatii: appointmentData.observatii || ''
            };

            const appointmentResponse = await fetch(`${process.env.REACT_APP_API_URL}/v1/public/programari/insert`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${process.env.REACT_APP_API_TOKEN}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(appointmentPayload)
            });

            if (!appointmentResponse.ok) {
                const errorText = await appointmentResponse.text();
                throw new Error(`Eroare la crearea programării: ${errorText}`);
            }

            const appointmentResult = await appointmentResponse.json();

            if (appointmentResult.ok) {
                setMessage('Programarea a fost creată cu succes! Veți primi o confirmare în curând.');
                // Reset form
                setPatientData({
                    nume: '',
                    prenume: '',
                    email: '',
                    telefon: '',
                    sex: ''
                });
                setAppointmentData({
                    id_specialitate: '',
                    id_medic: '',
                    data: '',
                    ora: '',
                    observatii: ''
                });
                setStep(1);
            } else {
                setError(appointmentResult.error || 'Eroare la crearea programării');
            }

        } catch (err) {
            console.error('Error creating appointment:', err);
            setError(err.message || 'Eroare la crearea programării');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{ padding: '2rem', maxWidth: '600px', margin: '0 auto', fontFamily: 'Arial, sans-serif' }}>
            <h1 style={{ textAlign: 'center', marginBottom: '2rem', color: '#006666', fontSize: '2rem' }}>
                Programare Online
            </h1>

            <p style={{ textAlign: 'center', marginBottom: '2rem', color: '#666', fontSize: '1.1rem' }}>
                Programați-vă ușor și rapid la clinica noastră medicală
            </p>

            {/* Progress indicator */}
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '2rem' }}>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem'
                }}>
                    <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        backgroundColor: step >= 1 ? '#006666' : '#ddd',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 'bold'
                    }}>
                        1
                    </div>
                    <div style={{
                        width: '60px',
                        height: '2px',
                        backgroundColor: step >= 2 ? '#006666' : '#ddd'
                    }}></div>
                    <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        backgroundColor: step >= 2 ? '#006666' : '#ddd',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 'bold'
                    }}>
                        2
                    </div>
                </div>
            </div>

            {/* Step labels */}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2rem', fontSize: '0.9rem', color: '#666' }}>
                <span style={{ color: step >= 1 ? '#006666' : '#666' }}>Date personale</span>
                <span style={{ color: step >= 2 ? '#006666' : '#666' }}>Detalii programare</span>
            </div>

            {error && (
                <div style={{
                    background: '#ffebee',
                    color: '#c62828',
                    padding: '1rem',
                    borderRadius: '8px',
                    marginBottom: '1rem',
                    border: '1px solid #ffcdd2',
                    fontSize: '0.9rem'
                }}>
                    {error}
                </div>
            )}

            {message && (
                <div style={{
                    background: '#e8f5e8',
                    color: '#2e7d32',
                    padding: '1rem',
                    borderRadius: '8px',
                    marginBottom: '1rem',
                    border: '1px solid #c8e6c9',
                    fontSize: '0.9rem'
                }}>
                    {message}
                </div>
            )}

            <div style={{
                background: 'white',
                padding: '2rem',
                borderRadius: '12px',
                boxShadow: '0 2px 12px rgba(0,0,0,0.1)',
                border: '1px solid #e0e0e0'
            }}>

                {/* Step 1: Patient Information */}
                {step === 1 && (
                    <div>
                        <h2 style={{ marginBottom: '1.5rem', color: '#333', fontSize: '1.3rem' }}>
                            Date personale
                        </h2>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                <div>
                                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', color: '#333' }}>
                                        Nume *
                                    </label>
                                    <input
                                        type="text"
                                        value={patientData.nume}
                                        onChange={(e) => handlePatientChange('nume', e.target.value)}
                                        style={{
                                            width: '100%',
                                            padding: '0.75rem',
                                            border: '1px solid #ddd',
                                            borderRadius: '8px',
                                            fontSize: '1rem'
                                        }}
                                        placeholder="Introduceți numele"
                                    />
                                </div>
                                <div>
                                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', color: '#333' }}>
                                        Prenume *
                                    </label>
                                    <input
                                        type="text"
                                        value={patientData.prenume}
                                        onChange={(e) => handlePatientChange('prenume', e.target.value)}
                                        style={{
                                            width: '100%',
                                            padding: '0.75rem',
                                            border: '1px solid #ddd',
                                            borderRadius: '8px',
                                            fontSize: '1rem'
                                        }}
                                        placeholder="Introduceți prenumele"
                                    />
                                </div>
                            </div>

                            <div>
                                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', color: '#333' }}>
                                    Email *
                                </label>
                                <input
                                    type="email"
                                    value={patientData.email}
                                    onChange={(e) => handlePatientChange('email', e.target.value)}
                                    style={{
                                        width: '100%',
                                        padding: '0.75rem',
                                        border: '1px solid #ddd',
                                        borderRadius: '8px',
                                        fontSize: '1rem'
                                    }}
                                    placeholder="email@example.com"
                                />
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1rem' }}>
                                <div>
                                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', color: '#333' }}>
                                        Telefon *
                                    </label>
                                    <input
                                        type="tel"
                                        value={patientData.telefon}
                                        onChange={(e) => handlePatientChange('telefon', e.target.value)}
                                        style={{
                                            width: '100%',
                                            padding: '0.75rem',
                                            border: '1px solid #ddd',
                                            borderRadius: '8px',
                                            fontSize: '1rem'
                                        }}
                                        placeholder="07xxxxxxxx"
                                    />
                                </div>
                                <div>
                                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', color: '#333' }}>
                                        Sex *
                                    </label>
                                    <select
                                        value={patientData.sex}
                                        onChange={(e) => handlePatientChange('sex', e.target.value)}
                                        style={{
                                            width: '100%',
                                            padding: '0.75rem',
                                            border: '1px solid #ddd',
                                            borderRadius: '8px',
                                            fontSize: '1rem'
                                        }}
                                    >
                                        <option value="">Selectați</option>
                                        <option value="F">Feminin</option>
                                        <option value="M">Masculin</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        <div style={{ textAlign: 'right', marginTop: '2rem' }}>
                            <button
                                onClick={handleNextStep}
                                disabled={loading}
                                style={{
                                    padding: '0.75rem 2rem',
                                    background: '#006666',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    fontSize: '1rem',
                                    fontWeight: 'bold',
                                    cursor: loading ? 'not-allowed' : 'pointer'
                                }}
                            >
                                Continuă
                            </button>
                        </div>
                    </div>
                )}

                {/* Step 2: Appointment Details */}
                {step === 2 && (
                    <div>
                        <h2 style={{ marginBottom: '1.5rem', color: '#333', fontSize: '1.3rem' }}>
                            Detalii programare
                        </h2>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                            <div>
                                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', color: '#333' }}>
                                    Specialitate *
                                </label>
                                <select
                                    value={appointmentData.id_specialitate}
                                    onChange={(e) => handleAppointmentChange('id_specialitate', e.target.value)}
                                    style={{
                                        width: '100%',
                                        padding: '0.75rem',
                                        border: '1px solid #ddd',
                                        borderRadius: '8px',
                                        fontSize: '1rem'
                                    }}
                                    disabled={loading}
                                >
                                    <option value="">Selectați specialitatea</option>
                                    {specialties.map(specialty => (
                                        <option key={specialty.id} value={specialty.id}>
                                            {specialty.denumire}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {appointmentData.id_specialitate && (
                                <div>
                                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', color: '#333' }}>
                                        Medic *
                                    </label>
                                    <select
                                        value={appointmentData.id_medic}
                                        onChange={(e) => handleAppointmentChange('id_medic', e.target.value)}
                                        style={{
                                            width: '100%',
                                            padding: '0.75rem',
                                            border: '1px solid #ddd',
                                            borderRadius: '8px',
                                            fontSize: '1rem'
                                        }}
                                        disabled={loading}
                                    >
                                        <option value="">Selectați medicul</option>
                                        {doctors.map(doctor => (
                                            <option key={doctor.id} value={doctor.id}>
                                                {doctor.nume} {doctor.prenume}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                <div>
                                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', color: '#333' }}>
                                        Data *
                                    </label>
                                    <input
                                        type="date"
                                        value={appointmentData.data}
                                        onChange={(e) => handleAppointmentChange('data', e.target.value)}
                                        min={new Date().toISOString().split('T')[0]}
                                        style={{
                                            width: '100%',
                                            padding: '0.75rem',
                                            border: '1px solid #ddd',
                                            borderRadius: '8px',
                                            fontSize: '1rem'
                                        }}
                                    />
                                </div>
                                <div>
                                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', color: '#333' }}>
                                        Ora *
                                    </label>
                                    <input
                                        type="time"
                                        value={appointmentData.ora}
                                        onChange={(e) => handleAppointmentChange('ora', e.target.value)}
                                        style={{
                                            width: '100%',
                                            padding: '0.75rem',
                                            border: '1px solid #ddd',
                                            borderRadius: '8px',
                                            fontSize: '1rem'
                                        }}
                                    />
                                </div>
                            </div>

                            <div>
                                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', color: '#333' }}>
                                    Observații (opțional)
                                </label>
                                <textarea
                                    value={appointmentData.observatii}
                                    onChange={(e) => handleAppointmentChange('observatii', e.target.value)}
                                    placeholder="Menționați orice detalii relevante..."
                                    style={{
                                        width: '100%',
                                        padding: '0.75rem',
                                        border: '1px solid #ddd',
                                        borderRadius: '8px',
                                        fontSize: '1rem',
                                        minHeight: '80px'
                                    }}
                                />
                            </div>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '2rem' }}>
                            <button
                                onClick={handlePrevStep}
                                disabled={loading}
                                style={{
                                    padding: '0.75rem 2rem',
                                    background: '#f5f5f5',
                                    color: '#333',
                                    border: '1px solid #ddd',
                                    borderRadius: '8px',
                                    fontSize: '1rem',
                                    cursor: loading ? 'not-allowed' : 'pointer'
                                }}
                            >
                                Înapoi
                            </button>
                            <button
                                onClick={createAppointment}
                                disabled={loading}
                                style={{
                                    padding: '0.75rem 2rem',
                                    background: loading ? '#ccc' : '#006666',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    fontSize: '1rem',
                                    fontWeight: 'bold',
                                    cursor: loading ? 'not-allowed' : 'pointer'
                                }}
                            >
                                {loading ? 'Se programează...' : 'Programează-mă'}
                            </button>
                        </div>
                    </div>
                )}

            </div>

            <div style={{ textAlign: 'center', marginTop: '2rem', color: '#666', fontSize: '0.9rem' }}>
                <p>* Câmpuri obligatorii</p>
                <p>Vă vom contacta pentru confirmarea programării în maximum 24 de ore.</p>
            </div>
        </div>
    );
}

export default ProgramariNew;
